import { ChangeEvent } from 'react';

export type EventHandler = (event: any | undefined) => void;

export type EventConfigCTX<T> = {
  [event: string]: EventHandler | T;
  context: T;
};

export type EventConfig = {
  [event: string]: EventHandler;
};

/**
 * Document or Window event registration
 */
const globalEventRegistration: EventConfig = {};

export const addEventHandler = (
  target: EventTarget,
  event: string,
  handler: EventHandler,
) => {
  target.addEventListener(event, handler);
  globalEventRegistration[event] = handler;
};

export const addDomEventHandler = (event: string, handler: EventHandler) => {
  document.addEventListener(event, handler);
  globalEventRegistration[event] = handler;
};

export const hasEventHandler = (event: string) => {
  const handler = globalEventRegistration[event];
  return !!handler;
};

export const removeEventHandler = (
  target: EventTarget,
  event: string,
  handler: EventHandler,
) => {
  target.removeEventListener(event, handler);
  delete globalEventRegistration[event];
};

export const removeDomEventHandler = (event: string, handler: EventHandler) => {
  document.removeEventListener(event, handler);
  delete globalEventRegistration[event];
};

export type TKInputEvent = ChangeEvent<HTMLInputElement | HTMLSelectElement>;

export type TKSelectEvent = ChangeEvent<HTMLSelectElement>;

export enum TKEvent {
  /** browser inactive */
  BLUR = 'blur',
  CLICK = 'click',
  FOCUS = 'focus',
  /** capture `Meta`, `Ctrl`, `Escape`, `Delete` */
  KEYDOWN = 'keydown',
  KEYUP = 'keyup',
  CONTEXTMENU = 'contextmenu',
  POINTERDOWN = 'pointerdown',
  POINTERUP = 'pointerup',
  POINTERMOVE = 'pointermove',
  /** 2025/05/02 */
  TRANSLATEND = 'translateend',
  TRANSLATEST = 'translatestart',
  ZOOMLEVELCHANGE = 'change:resolution',
  ZOOMOUT = 'zoomOut',
  ZOOOMIN = 'zoomIn',
  ZOOMBY = 'zoomBy',
  /** === draw a new fitting(dot) on map */
  NEWELEMENT = 'newElement',
  /** === draw a new length(line) on map */
  NEWLINE = 'newLine',
  /** correct selected feature from original correction modal @deprecated */
  CORRECTELEMENT = 'correctElement',
  /** correct symbol in total correction table */
  CORRECTSYMBOLSILENT = 'correctSymbolSilent',
  /** confirm symbol, mark symbol property: user_validated:1 */
  CONFIRMSYMBOL = 'confirmSymbol',
  CONFIRMSYMBOLS = 'confirmSymbols',
  MAPLOADSTART = 'loadstart',
  MAPLOADEND = 'loadend',
  MAPLOADERROR = 'error',
  /** MAP start zooming or panning */
  MAPMOVESTART = 'movestart',
  /** Map zooming/panning to the end */
  MAPMOVENDED = 'moveend',
  /** Map rendered completed */
  MAPPOSTRENDER = 'postrender',
  /** Map render start */
  MAPPRERENDER = 'prerender',
  /** tile layer Zoomify dispatched event */
  MAPTILELOADERROR = 'tileloaderror',
  /**
   * editor dispatched event
   * @deprecated no longer in use
   */
  MAPLEVELCHANGED = 'mapLevelChanged',
  FEATURECOPY = 'featureCopy',
  /** open property panel */
  FEATURESELECTED = 'featureSelected',
  /**
   * close property panel by:
   * - clicking blank space of map, or
   * - feature deletion
   */
  FEATUREDESELECT = 'featureDeselect',
  FEATUREDELETED = 'featureDeleted',
  FEATUREDELETEALL = 'featureDeleteAll',
  FEATUREREPLACE = 'featureReplace',
  /**
   * deselect all from `clear selection` tool
   * @2025/04/17
   */
  DESELECTALL = 'deselectAll',
  /** excluded from selection */
  EXCLUDESELECTION = 'excludeSelection',
  /** ? */
  MODIFYFEATUREMODE = 'modifyFeatureMode',
  MOVEFEATUREMODE = 'moveFeatureMode',
  AUTOFEATUREMODE = 'autoFeatureMode',
  REVIEWFITTINGS = 'reviewFittings',
  REVIEWCANCELED = 'reviewCanceled',
  REVIEWAPPLIED = 'reviewApplied',
  REVIEWSYSTEMS = 'reviewSystems',
  FLYTOFEATURE = 'flyToFeature',
  /** fly to feature from total correction table */
  FLYTOFEATUREONLEFT = 'flyToFeatureOnLeft',
  /** notify acitve row change on review panel - 2025/01/29 */
  SELECTSYMBOLROW = 'selectSymbolRow',
  RECHECKFEATURE = 'reCheckFeature',
  UNCHECKFEATURE = 'unCheckFeature',
  FORCEUPDATEMAPSIZE = 'forceUpdateMapSize',
  /** @deprecated in favor of `OPEN_TOTAL_TABLE - @2023/12/07` */
  POPUPDUCTCORRECTMODAL = 'popupDuctCorrect',
  POPUPUNSAVINGMODAL = 'popupUnsavingConfirm',
  PAGESELECTED = 'pageSelected',
  PAGEDESELECT = 'pageDeselected',
  PAGESDISCARD = 'pagesDiscarded',
  PAGESELECTALL = 'pageSelectAllNew',
  PAGEDESELECTALL = 'pageDeselectAllNew',
  /** AI job request sent */
  JOBSTARTED = 'jobStarted',
  /** job status changed - 2024/02/23 */
  JOB_STATUS_CHANGE = 'jobStatusChanged',
  /** job progress notification - 2024/02/23 */
  JOB_PROGRESS_CHANGE = 'jobProgressChanged',
  /** job done with status `complete` - 2024/04/15 */
  JOB_STATUS_DONE = 'jobStatusComplete',
  /** File extraction, and AI job status polling */
  STARTPOLLING = 'startPolling',
  /** AI job  COMPLETED @deprecated */
  STOPPOLLING = 'stopPolling',
  /** AI job error */
  ERRORPOLLING = 'errorPolling',
  FILEXTRACTCOMPLETE = 'fileExtractComplete',
  FILEXTRACTFAILURE = 'fileExtractFailure',
  /** filter symbols by item type */
  FILTERSYMBOLS = 'filterSymbols',
  /** filter symbols by `system` along with item type - 2025/04/02 */
  FILTERWITHSYSTEM = 'filterWithSystem',
  /** cover symbols with white */
  REVERSYMBOLS = 'reverseSymbols',
  /** hide text toggling */
  REVERSESIZEDISPLAY = 'reverseSizeText',
  /**
   * Real saving event from `useTopNavBar`, then be processed in `useSymbolSaving`
   */
  SAVECHANGES = 'saveChanges',
  /**
   * Saving changed symbols done!
   */
  SAVECHANGESCOMPLETED = 'saveChangesCompleted',
  /**
   * == intermediate event ==
   *
   * then relay to `SAVECHANGES` event.
   *
   * Save correction from table view with `silent` mode or non-`silent` mode.
   */
  SAVETOTALCORRECTIONS = 'saveTotalCorrections',
  NEWSYMBOLSAVED = 'newSymbolSaved',
  /** delete one symbol from store in `reducer` by page & symbol */
  CLEARSYMBOL_FROM_STORE = 'clearSymbolFromStore',
  /** draw new symbol and cache it to reducer store */
  ADDSYMBOL_TO_STORE = 'addSymbolToStore',
  /** move/resize symbol, and correction properties */
  MODSYMBOL_IN_STORE = 'upadateSymbolInStore',
  /** restore moved symbol from store - @2025/05/02 */
  RESTORE_LAST_SYMBOL = 'undoLastSymbolInStore',
  /**
   * synchronize correction of multiple symbols in store,
   * if event detail is empty, then schedule a auto-saving
   * @date 2024/09/10
   */
  SYNCBATCH_IN_STORE = 'synchronizeBatchInStore',
  /** stroke all the symbols of selected system */
  HIGHLIGHTSYSTEM = 'highlightSystem',
  /** cancel highlighted symbols */
  DELIGHTSYSTEM = 'delightSystem',
  /** batch features selected by drag selection */
  BATCHSELECTION = 'batchFeatureSelected',
  /** cache dimension info update to features & symbols to save later */
  BATCHCORRECTION = 'batchFeatureCorrected',
  /** quietly refresh(re-fetch) page symbols AFTER changes(or drawing scale) saved */
  REFRESHSYMBOLS = 'refreshSymbols',
  /**
   * reload symbols after page patched, need spinner support
   * @deprecated in favor of `refreshSymbols`
   */
  RELAODSYMBOLS = 'reloadSymbols',
  /** get mouse position or pulling distance */
  MOUSE_REVEALED = 'mouseRevealed',
  /** retry AI job */
  RESTART_AI_JOB = 'retryAIJob',
  /** set magic brush setting to map */
  APPLY_BRUSH_SETTING = 'applyBrushSetting',
  /** show symbols same as table */
  SYNC_SYMBOLS_FROM_TABLE = 'syncSymbolsFromTable',
  /**
   * mute symbol store changed, unset the global `change` state from `useSymbolsEditStore`
   */
  MUTE_SYMBOL_CHANGE = 'muteSymbolChangeState',
  /** toggle item type filter by select symbol id from table view */
  TOGGLE_ITEM_TYPE = 'toggleItemTypeFromSymbolId',
  /** notify drawing that total table opened, then zoom-in drawing to make it larger */
  TOTAL_TABLE_OPENED = 'totalTableOpened',
  /** notify `TotalReviewOpener` to open table correction panel */
  OPEN_TOTAL_TABLE = 'openTotalReviewTable',
  /**
   * Cancel drawing tool, listened in `useNewElementTools`
   * @2025/04/19
   */
  CANCEL_DRAWING_TOOL = 'cancelDrawingTool',
  /**
   * cancel brush tool, deselect tool from map-control section
   * @2025/06/09
   */
  CANCEL_BRUSH_TOOL = 'cancelBrushTool',
  /** cancel saving schedule */
  CANCEL_SAVING_CHANGES = 'holdOffScheduledSaving',
  /** open feature properties panel */
  OPEN_FEATURE_PROPERTIES = 'openFeaturePropertiesPanel',
  /** open batch selected features panel */
  OPEN_BATCH_SELECTIONS = 'openBatchSelectionPanel',
  /**
   * close feature panels
   * @deprecated not in use
   */
  CLOSE_SIDE_PANEL = 'closeFeatureSelectionPanel',
  /** large file uploading progress */
  UPLOAD_PROGRESS = 'uploadProgress',
  /** schedule an auto-saving on review panel closing - @2025/07/30 */
  SCHEDULE_AUTO_SAVING = 'scheduleAutoSaving',
  // ======  TOOLS EVENTS SECTION ====================
  /** == enable `ruler` tool == */
  RULERENABLED = 'rulerEnabled',
  /** == disable `ruler` tool == */
  RULERDISABLED = 'rulerDisabled',
  /** size change notification to re-render symbols - @2025/08/21 */
  SIZE_FILTER_CHANGED = 'sizeFileChanged',
  /** open size merge tool */
  SIZE_MERGE_OPEN = 'sizeMergeOpen',
  /** close size merge tool */
  SIZE_MERGE_CLOSE = 'sizeMergeClose',
  /** note tool enabled */
  NOTE_TOOL_ENABLED = 'noteToolEnabled',
  /** note tool disabled */
  NOTE_TOOL_DISABLED = 'noteToolDisabled',
  /** draw an END_CAP symbol in the center of LENGTH symbol - @2025/11/06 */
  ENDCAP_ATTACH = 'endcapAttach',
  ENDCAP_DETACH = 'endcapDetach',
  /** TODO: ... */
}
